<template>
  <div>
    <form>
      <div class="p-fluid p-formgrid p-grid">
        <div class="p-field p-col-12 p-md-2">
          <label for="fromDate">From Date</label>
          <date-picker v-model="fromDate" valueType="format" v-if="!englishDateEnabled" />
          <InputMask mask="9999-99-99" type="text" v-else class="nepali-calendar" id="fromDate" v-model="fromDate" />
        </div>
        <div class="p-field p-col-12 p-md-2">
          <label for="toDate">To Date</label>
          <date-picker v-model="toDate" valueType="format" v-if="!englishDateEnabled" />
          <InputMask mask="9999-99-99" type="text" v-else class="nepali-calendar" id="toDate" v-model="toDate" />
        </div>
        <div class="p-field p-col-12 p-md-4">
          <label for="users">Users</label>
          <MultiSelect 
            v-model="selectedUsers" 
            :options="userOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="Select Users"
            :showCheckbox="true"
            display="chip"
            class="multiselect-custom"
            :filter="true"
            :loading="loadingUsers"
          >
            <template #header>
              <div class="p-py-2 p-px-3">
                <Checkbox 
                  v-model="allSelected" 
                  :binary="true" 
                  @change="toggleSelectAll"
                />
                <span class="p-ml-2">Select All</span>
              </div>
            </template>
            <template #option="slotProps">
              <div>{{ slotProps.option.name }} <small class="p-text-muted">({{ slotProps.option.username }})</small></div>
            </template>
          </MultiSelect>
        </div>        
        <div class="p-field p-col-12 p-md-1 p-mt-5">
          <Button 
            @click.prevent="searchReport" 
            class="p-button-raised" 
            label="Search" 
            icon="pi pi-search"
            :loading="loading"
          />
        </div>
      </div>
    </form>    
    <div class="chart-container p-grid p-mt-5">
      <div class="p-col-12 p-md-6">
        <div class="chart-wrapper">
          <h4>User Distribution (Bar)</h4>
          <div class="chart-subcontainer">
            <div v-if="loading" class="chart-loading">
              <i class="pi pi-spin pi-spinner"></i> Loading...
            </div>
            <div v-else-if="!hasChartData" class="chart-empty">No data</div>
            <canvas ref="stageBarChart" :style="{ display: loading || !hasChartData ? 'none' : 'block' }"></canvas>
          </div>
        </div>
      </div>
      <div class="p-col-12 p-md-6">
        <div class="chart-wrapper">
          <h4>User Percentage (Pie)</h4>
          <div class="chart-subcontainer">
            <div v-if="loading" class="chart-loading">
              <i class="pi pi-spin pi-spinner"></i> Loading...
            </div>
            <div v-else-if="!hasChartData" class="chart-empty">No data</div>
            <canvas ref="stagePieChart" :style="{ display: loading || !hasChartData ? 'none' : 'block' }"></canvas>
          </div>
        </div>
      </div>
      <div class="p-col-12">
        <div class="chart-wrapper">
          <h4>User Follow-ups (Line)</h4>
          <div class="chart-subcontainer">
            <div v-if="loading" class="chart-loading">
              <i class="pi pi-spin pi-spinner"></i> Loading...
            </div>
            <div v-else-if="!hasChartData" class="chart-empty">No data</div>
            <canvas ref="stageLineChart" :style="{ display: loading || !hasChartData ? 'none' : 'block' }"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import DatePicker from 'vue2-datepicker'
import InputMask from 'primevue/inputmask'
import Button from 'primevue/button'
import MultiSelect from 'primevue/multiselect'
import Checkbox from 'primevue/checkbox'
import $ from 'jquery'

export default {
  name: 'CRMUserWiseReport',
  components: {
    DatePicker,
    InputMask,
    Button,
    MultiSelect,
    Checkbox
  },
  data() {
    return {
      fromDate: null,
      toDate: null,
      englishDateEnabled: true,
      selectedUsers: [],
      userOptions: [],
      allSelected: false,
      loadingUsers: false,
      barChart: null,
      pieChart: null,
      lineChart: null,
      loading: false,
      hasChartData: false,
      colorPalette: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
        '#9966FF', '#FF9F40', '#8AC926', '#1982C4', '#6A4C93'
      ]
    }
  },
  watch: {
    selectedUsers(newVal) {
      this.allSelected = newVal.length === this.userOptions.length
    }
  },
  async mounted() {
    await this.loadUsers()
    this.checkEnglish()
    if (!this.$store.getters.englishDate || this.$store.state.isEnglishDateEnabled === 'false') {
      this.nepaliDate()
    }
    if (!this.$store.getters.englishDate && this.salesChallan === undefined ||
        this.$store.state.isEnglishDateEnabled === 'false') {
      this.setNepaliDate()
    }
    if (this.$store.state.isEnglishDateEnabled && this.$store.state.isEnglishDateEnabled !== 'false') {
      this.setEnglishDate()
    }
    this.initializeDates()
  },
  methods: {
    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await this.$store.dispatch('loadUser')
        if (response.data.data) {
          this.userOptions = response.data.data
            .filter(user => user)
            .map(user => ({
              id: user.id,
              name: user.name || user.username || 'Unknown User',
              username: user.username
            }))
        }
      } catch (error) {
        console.error('Failed to load users:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load user list',
          life: 3000
        })
      } finally {
        this.loadingUsers = false
      }
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    toggleSelectAll() {
      this.allSelected = !this.allSelected
      this.selectedUsers = this.allSelected ? [...this.userOptions] : []
    },
    async searchReport() {
      this.loading = true
      this.hasChartData = false
      this.cleanupCharts()
      try {
        const userIds = this.selectedUsers.length === 0
        ? null
        : this.selectedUsers.filter(id => id != null && id !== '')
        const data = {
          from: this.fromDate,
          to: this.toDate,
          userIds: userIds
        }
        const response = await this.$store.dispatch('loadCRMUserWiseGraphData', data)
        console.log('API Response:', response)
        this.createCharts(response)
      } catch (error) {
        console.error('Error loading CRM user data:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user-wise data',
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },
    cleanupCharts() {
      if (this.barChart) {
        this.barChart.destroy()
        this.barChart = null
      }
      if (this.pieChart) {
        this.pieChart.destroy()
        this.pieChart = null
      }
      if (this.lineChart) {
        this.lineChart.destroy()
        this.lineChart = null
      }
    },
    createCharts(responseData) {
      let labels = []
      let counts = []
      let backgroundColors = []

      if (!responseData) {
        this.hasChartData = false
        return
      }
      if (responseData.labels && responseData.datasets.length > 0) {
        labels = responseData.labels || []
        counts = responseData.datasets[0].data || []
        backgroundColors = responseData.datasets[0].backgroundColor || []
      } else if (typeof responseData === 'object') {
        Object.entries(responseData).forEach(([userId, count], index) => {
          const user = this.userOptions.find(u => String(u.id) === String(userId))
          if (count !== undefined && count !== null) {
            labels.push(user.name || `User ${userId}`)
            counts.push(Number(count))
            backgroundColors.push(this.colorPalette[index % this.colorPalette.length])
          }
        })
      }
      if (labels.length === 0 || counts.length === 0) {
        this.hasChartData = false
        return
      }
      this.hasChartData = true
      this.cleanupCharts()
      this.createBarChart(labels, counts, backgroundColors)
      this.createPieChart(labels, counts, backgroundColors)
      this.createLineChart(labels, counts, backgroundColors)
    },
    createLineChart(labels, counts, colors) {
      const canvas = this.$refs.stageLineChart
      if (!canvas) return
      if (this.lineChart) {
        this.lineChart.destroy()
      }
      this.lineChart = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Follow-up Count',
            data: counts,
            backgroundColor: colors.map(c => this.hexToRgba(c, 0.2)),
            borderColor: colors,
            borderWidth: 3,
            pointBackgroundColor: colors,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6,
            fill: true,
            tension: 0.4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20,
                boxWidth: 8,
                font: { size: 11 }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { precision: 0 }
            },
            x: {
              ticks: {
                maxRotation: 45,
                minRotation: 45
              }
            }
          }
        }
      })
    },
    createBarChart(labels, counts, colors) {
      const canvas = this.$refs.stageBarChart
      if (!canvas) return
      if (this.barChart) {
        this.barChart.destroy()
      }
      const datasets = labels.map((label, index) => ({
        label: label,
        data: [counts[index]],
        backgroundColor: colors[index],
        borderColor: colors[index],
        borderWidth: 1
      }))
      this.barChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: [''],
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20,
                boxWidth: 10,
                font: { size: 12 }
              }
            }
          },
          scales: {
            x: {
              display: true,
              title: { display: false },
              ticks: { autoSkip: true }
            },
            y: {
              beginAtZero: true,
              ticks: { precision: 0 }
            }
          }
        }
      })
    },
    createPieChart(labels, counts, colors) {
      const canvas = this.$refs.stagePieChart
      if (!canvas) return

      this.pieChart = new Chart(canvas, {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            data: counts,
            backgroundColor: colors,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'right' } }
        }
      })
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    initializeDates() {
      this.checkEnglish()
      if (!this.$store.getters.englishDate || this.$store.state.isEnglishDateEnabled === 'false') {
        this.nepaliDate()
        this.setNepaliDate()
      } else {
        this.setEnglishDate()
      }
    },
    setEnglishDate() {
      const today = new Date()
      this.fromDate = today.toISOString().slice(0, 10)
      this.toDate = today.toISOString().slice(0, 10)
    },
    setNepaliDate() {
      if (this.$store.getters.nepaliNewDate !== '') {
        this.fromDate = this.$store.getters.nepaliNewDate
        this.toDate = this.$store.getters.nepaliNewDate
      }
    },
    checkEnglish() {
      if (this.$store.state.isEnglishDateEnabled === false ||
          this.$store.state.isEnglishDateEnabled === 'false') {
        this.englishDateEnabled = true
      } else {
        this.englishDateEnabled = false
      }
    },
    nepaliDate() {
      $('#fromDate').nepaliDatePicker({
        ndpYear: true,
        ndpMonth: true,
        ndpYearCount: 10,
        disableBefore: this.$store.state.startDate,
        disableAfter: this.$store.state.endDate,
        onChange: () => {
          this.fromDate = $('#fromDate').val()
        }
      })
      $('#toDate').nepaliDatePicker({
        ndpYear: true,
        ndpMonth: true,
        ndpYearCount: 10,
        disableBefore: this.$store.state.startDate,
        disableAfter: this.$store.state.endDate,
        onChange: () => {
          this.toDate = $('#toDate').val()
        }
      })
    }
  },
  beforeDestroy() {
    this.cleanupCharts()
  }
}
</script>

<style scoped>
/* Same styles as before */
.multiselect-custom {
  font-size: 1.2rem;
  min-height: 40px;
}

::v-deep .p-multiselect-label {
  padding: 0.75rem !important;
  font-size: 1.2rem !important;
  min-height: 40px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chart-container {
  margin-top: 2rem;
}

.chart-wrapper {
  padding: 1rem;
}

.chart-subcontainer {
  height: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem;
  position: relative;
}

.chart-loading, .chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1.2rem;
  color: #6c757d;
}

.chart-loading i {
  margin-right: 8px;
  font-size: 1.5rem;
}

h4 {
  color: #444;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
  font-size: 1.4rem;
}

::v-deep .p-multiselect-header {
  padding: 0.5rem !important;
  border-bottom: 1px solid #dee2e6;
}
</style>



// WEBPACK FOOTER //
// crmUserWiseGraph.vue?f12aa47a